import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UserCog, Plus, Pencil, Trash2, X, Save, Search, KeyRound, ArrowLeft,
  Loader2, ShieldCheck, ToggleLeft, ToggleRight, Image as ImageIcon, Activity
} from "lucide-react";
import { toast } from "sonner";
import { staffAdminApi } from "@/library/lib/staffApi";
import { getToken } from "@/library/lib/api";
import { resolveLogoUrl } from "@/library/lib/settings";

const blank = { username: "", password: "", full_name: "", employee_id: "", email: "", phone: "", job_title: "", department: "", photo_url: "" };

export default function StaffAdmin() {
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [newTitle, setNewTitle] = useState("");
  const [showTitleMgr, setShowTitleMgr] = useState(false);

  useEffect(() => {
    if (!getToken()) { nav("/library/admin/login"); return; }
    reload();
  }, [nav]);

  const reload = async () => {
    setLoading(true);
    try {
      const [u, t] = await Promise.all([
        staffAdminApi.get("/users"), staffAdminApi.get("/job-titles"),
      ]);
      setUsers(u.data); setTitles(t.data);
    } catch (err) {
      if (err.response?.status === 401) nav("/library/admin/login");
      else toast.error("تعذر التحميل");
    } finally { setLoading(false); }
  };

  const filtered = users.filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [u.username, u.full_name, u.email, u.employee_id, u.job_title, u.department].some(v => (v || "").toLowerCase().includes(s));
  });

  const openNew = () => { setForm({ ...blank, job_title: titles[0]?.name || "" }); setShowNew(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: "" }); };

  const uploadPhoto = async (uid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await staffAdminApi.post(`/users/${uid}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.photo_url;
  };

  const create = async () => {
    if (!form.username || !form.password || !form.full_name || !form.job_title) {
      toast.error("الحقول الإلزامية: اسم المستخدم، كلمة المرور، الاسم الرباعي، المسمى الوظيفي");
      return;
    }
    try {
      const { data } = await staffAdminApi.post("/users", form);
      toast.success("تم إنشاء الموظف");
      setShowNew(false); setForm(blank);
      await reload();
      return data;
    } catch (err) { toast.error(err.response?.data?.detail || "تعذر الإنشاء"); }
  };

  const update = async () => {
    const payload = { ...form };
    delete payload.username; delete payload.id; delete payload.created_at;
    if (!payload.password) delete payload.password;
    try {
      await staffAdminApi.put(`/users/${editing.id}`, payload);
      toast.success("تم التحديث");
      setEditing(null);
      await reload();
    } catch (err) { toast.error(err.response?.data?.detail || "تعذر التحديث"); }
  };

  const toggleActive = async (u) => {
    try {
      await staffAdminApi.put(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "تم تعطيل الحساب" : "تم تفعيل الحساب");
      await reload();
    } catch { toast.error("تعذر التغيير"); }
  };

  const resetPw = async (u) => {
    const pw = prompt(`كلمة المرور الجديدة للموظف "${u.full_name}":`);
    if (!pw) return;
    try {
      await staffAdminApi.put(`/users/${u.id}`, { password: pw });
      toast.success("تم إعادة تعيين كلمة المرور");
    } catch (err) { toast.error(err.response?.data?.detail || "تعذر التغيير"); }
  };

  const doDelete = async (u) => {
    if (!window.confirm(`حذف الموظف "${u.full_name}" وجميع ملفاته؟`)) return;
    try {
      await staffAdminApi.delete(`/users/${u.id}`);
      toast.success("تم الحذف");
      await reload();
    } catch { toast.error("تعذر الحذف"); }
  };

  const onPhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      if (editing) {
        const url = await uploadPhoto(editing.id, file);
        setForm((f) => ({ ...f, photo_url: url }));
        toast.success("تم رفع الصورة");
      } else {
        toast.info("سترفع الصورة بعد حفظ بيانات الموظف — أنشئ الحساب أولاً ثم رفع الصورة.");
      }
    } catch { toast.error("تعذر الرفع"); }
  };

  const addTitle = async () => {
    if (!newTitle.trim()) return;
    try {
      await staffAdminApi.post("/job-titles", { name: newTitle.trim() });
      setNewTitle("");
      await reload();
      toast.success("تمت الإضافة");
    } catch { toast.error("تعذر الإضافة"); }
  };

  const delTitle = async (t) => {
    if (!window.confirm(`حذف المسمى "${t.name}"؟`)) return;
    try {
      await staffAdminApi.delete(`/job-titles/${t.id}`);
      await reload();
    } catch { toast.error("تعذر الحذف"); }
  };

  return (
    <div className="library-scope min-h-screen bg-[#F5F7FA]" dir="rtl">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-brand-dark text-[15px]">إدارة منسوبي المدرسة</div>
              <div className="text-[11px] text-slate-500">إنشاء وتعديل حسابات الموظفين</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => nav("/library/admin/staff/log")} data-testid="btn-activity-log" className="text-xs font-heading px-3 py-1.5 rounded-lg text-brand border border-brand/20 hover:bg-brand/5 flex items-center gap-1.5">
              <Activity className="w-4 h-4" /> سجل العمليات
            </button>
            <button onClick={() => nav("/library/admin")} className="text-xs font-heading px-3 py-1.5 rounded-lg text-brand border border-brand/20 hover:bg-brand/5 flex items-center gap-1.5">
              <ArrowLeft className="w-4 h-4 rotate-180" /> لوحة المكتبة
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-5">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث في الاسم، اسم المستخدم، البريد، المسمى..." data-testid="staff-admin-search" className="w-full pe-9 ps-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-brand outline-none" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowTitleMgr(true)} data-testid="btn-manage-titles" className="text-xs font-heading px-3 py-2 rounded-lg text-brand border border-brand/20 hover:bg-brand/5">
              المسميات الوظيفية ({titles.length})
            </button>
            <button onClick={openNew} data-testid="btn-new-staff" className="text-xs font-heading px-3 py-2 rounded-lg bg-brand text-white hover:bg-brand-light flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> موظف جديد
            </button>
          </div>
        </div>

        <div className="premium-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="staff-users-table">
              <thead>
                <tr className="bg-brand text-white text-xs">
                  <th className="px-3 py-3 text-right w-12">#</th>
                  <th className="px-3 py-3 text-right">الموظف</th>
                  <th className="px-3 py-3 text-right">المسمى / القسم</th>
                  <th className="px-3 py-3 text-right">التواصل</th>
                  <th className="px-3 py-3 text-center">المجلدات</th>
                  <th className="px-3 py-3 text-center">الملفات</th>
                  <th className="px-3 py-3 text-center">الحالة</th>
                  <th className="px-3 py-3 text-center w-40">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="px-3 py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 inline animate-spin mx-2" /> جاري التحميل...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-12 text-center text-slate-400">لا يوجد موظفون بعد.</td></tr>
                ) : filtered.map((u, i) => (
                  <tr key={u.id} className="odd:bg-white even:bg-slate-50/60 hover:bg-brand/5" data-testid={`row-${u.username}`}>
                    <td className="px-3 py-2.5 text-slate-500 tabular-nums text-xs">{i + 1}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5">
                        {u.photo_url ? (
                          <img src={resolveLogoUrl(u.photo_url)} alt="" className="w-9 h-9 rounded-lg object-cover ring-1 ring-slate-200" />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-brand/10 text-brand flex items-center justify-center"><UserCog className="w-4 h-4" /></div>
                        )}
                        <div>
                          <div className="font-heading font-bold text-brand-dark text-[13px]">{u.full_name}</div>
                          <div className="text-[11px] text-slate-500">@{u.username} {u.employee_id && <span className="text-slate-400"> · #{u.employee_id}</span>}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      <div className="text-slate-800">{u.job_title || "—"}</div>
                      {u.department && <div className="text-[11px] text-slate-500">{u.department}</div>}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 tabular-nums">
                      {u.email && <div className="truncate max-w-[180px]">{u.email}</div>}
                      {u.phone && <div>{u.phone}</div>}
                    </td>
                    <td className="px-3 py-2.5 text-center text-slate-700 tabular-nums text-xs">{u.folder_count || 0}</td>
                    <td className="px-3 py-2.5 text-center text-slate-700 tabular-nums text-xs">{u.file_count || 0}</td>
                    <td className="px-3 py-2.5 text-center">
                      <button onClick={() => toggleActive(u)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${u.active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`} data-testid={`toggle-${u.username}`}>
                        {u.active ? <ToggleRight className="w-3 h-3" /> : <ToggleLeft className="w-3 h-3" />}
                        {u.active ? "مفعّل" : "موقوف"}
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEdit(u)} title="تعديل" className="p-1.5 rounded-md text-brand hover:bg-brand/5"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => resetPw(u)} title="إعادة تعيين كلمة المرور" className="p-1.5 rounded-md text-amber-600 hover:bg-amber-50"><KeyRound className="w-3.5 h-3.5" /></button>
                        <button onClick={() => doDelete(u)} title="حذف" className="p-1.5 rounded-md text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {(showNew || editing) && (
        <Modal title={editing ? `تعديل: ${editing.full_name}` : "موظف جديد"} onClose={() => { setShowNew(false); setEditing(null); }} wide>
          <UserForm form={form} setForm={setForm} titles={titles} editing={editing} onPhotoChange={onPhotoChange} />
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => { setShowNew(false); setEditing(null); }} className="px-4 py-1.5 text-sm rounded-lg text-slate-600 hover:bg-slate-100">إلغاء</button>
            <button onClick={editing ? update : create} data-testid="btn-save-staff" className="px-4 py-1.5 text-sm rounded-lg bg-brand text-white hover:bg-brand-light flex items-center gap-1.5">
              <Save className="w-3.5 h-3.5" /> {editing ? "حفظ التعديلات" : "حفظ"}
            </button>
          </div>
        </Modal>
      )}

      {showTitleMgr && (
        <Modal title="إدارة المسميات الوظيفية" onClose={() => setShowTitleMgr(false)}>
          <div className="flex gap-2 mb-3">
            <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="مسمى جديد..." className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-brand outline-none" />
            <button onClick={addTitle} className="px-3 py-2 text-sm rounded-lg bg-brand text-white hover:bg-brand-light flex items-center gap-1"><Plus className="w-4 h-4" /> إضافة</button>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {titles.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
                <span className="text-sm text-slate-700">{t.name}</span>
                <button onClick={() => delTitle(t)} className="p-1 rounded text-rose-500 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

function UserForm({ form, setForm, titles, editing, onPhotoChange }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2 flex items-center gap-3">
        <div className="shrink-0">
          {form.photo_url ? (
            <img src={resolveLogoUrl(form.photo_url)} alt="" className="w-16 h-16 rounded-xl object-cover ring-1 ring-slate-200" />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-brand/10 text-brand flex items-center justify-center"><ImageIcon className="w-6 h-6" /></div>
          )}
        </div>
        <label className="text-xs font-heading text-brand cursor-pointer border border-brand/20 rounded-lg px-3 py-1.5 hover:bg-brand/5">
          <input type="file" accept="image/*" onChange={onPhotoChange} className="hidden" data-testid="upload-photo" /> رفع صورة شخصية {editing ? "" : "(بعد الحفظ)"}
        </label>
      </div>
      <F label="اسم المستخدم *" v={form.username} on={set("username")} disabled={!!editing} test="staff-input-username" />
      <F label={editing ? "كلمة مرور جديدة (اختياري)" : "كلمة المرور *"} v={form.password} on={set("password")} type="password" test="staff-input-password" />
      <F label="الاسم الرباعي *" v={form.full_name} on={set("full_name")} test="staff-input-fullname" />
      <F label="الرقم الوظيفي" v={form.employee_id} on={set("employee_id")} />
      <F label="البريد الإلكتروني" v={form.email} on={set("email")} type="email" />
      <F label="رقم الجوال" v={form.phone} on={set("phone")} />
      <div>
        <label className="block text-xs font-bold text-slate-600 mb-1">المسمى الوظيفي *</label>
        <select value={form.job_title} onChange={set("job_title")} data-testid="staff-input-title" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-brand outline-none bg-white">
          <option value="">اختر المسمى...</option>
          {titles.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
        </select>
      </div>
      <F label="القسم" v={form.department} on={set("department")} />
    </div>
  );
}

const F = ({ label, v, on, type = "text", test, disabled }) => (
  <div>
    <label className="block text-xs font-bold text-slate-600 mb-1">{label}</label>
    <input value={v || ""} onChange={on} disabled={disabled} type={type} data-testid={test} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-brand outline-none disabled:bg-slate-50" />
  </div>
);

function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} p-5 max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading font-extrabold text-brand-dark">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}
